import React, { useMemo } from 'react';
import { Field, EditView, LinkButton, useHttp } from '@builtbypixel/nucleus';
import {
    Text,
    Box,
    Alert,
    Tabs,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Grid,
    Button,
    Flex,
    PopConfirm,
    useToast,
    Card,
} from '@builtbypixel/plasma';
import { useFormContext, useWatch } from 'react-hook-form';
import setup from './setup';
import dayjs from 'dayjs';

const SectionTitle = ({ children }) => (
    <Text mb='20px' fontSize='1.4em'>
        {children}
    </Text>
);

const Column = ({ children }) => (
    <Box borderBottom='1px' borderColor='global.borderColour' py='8px'>
        {children}
    </Box>
);

const Fields = () => {
    const { watch, data } = useFormContext();

    const payments = useWatch({ name: 'payments' });
    const future_payments = useWatch({ name: 'upcoming_payments' });
    const toast = useToast();
    const Http = useHttp();

    const pause = () => {
        Http.post(`/subscriptions/${data.id}/deactivate`)
            .then((res) => {
                window.location.reload();
            })
            .catch((err) => {
                toast({
                    status: 'error',
                    message: err.response.data.error.message,
                });
            });
    };

    const activate = () => {
        Http.post(`/subscriptions/${data.id}/activate`)
            .then((res) => {
                window.location.reload();
            })
            .catch((err) => {
                toast({
                    status: 'error',
                    message: err.response.data.error.message,
                });
            });
    };

    const cancel = () => {
        Http.post(`/subscriptions/${data.id}/cancel`)
            .then((res) => {
                window.location.reload();
            })
            .catch((err) => {
                toast({
                    status: 'error',
                    message: err.response.data.error.message,
                });
            });
    };

    return (
        <Box p='20px' w='100%' maxWidth='700px'>
            <Tabs orientation='horizontal'>
                <TabList>
                    <Tab>Summary</Tab>
                    <Tab>Completed Payments</Tab>
                    <Tab>Upcoming Payments</Tab>
                </TabList>
                <TabPanels>
                    <TabPanel>
                        <Card title='Mosque Information' mt='10px' mb='20px'>
                            <Box p='20px'>
                                <Text fontSize='1.2em' fontWeight='semibold'>
                                    {data?.mosque.name}
                                </Text>
                                <Text>
                                    {data?.user.first_name}{' '}
                                    {data?.user.last_name} - {data?.user.email}
                                </Text>
                                <LinkButton
                                    to={`/mosques/edit/${data?.mosque.id}`}
                                >
                                    <Button
                                        mt='15px'
                                        size='sm'
                                        variant='outline'
                                    >
                                        View Mosque Profile
                                    </Button>
                                </LinkButton>
                            </Box>
                        </Card>
                        <Card title='Trial Status' mb='20px'>
                            <Box w='100%' p='20px'>
                                {data?.free_trial_end_date ? (
                                    <Alert status='success' variant='solid'>
                                        Free trial active
                                    </Alert>
                                ) : (
                                    <Alert status='error'>
                                        Free trial has ended
                                    </Alert>
                                )}
                            </Box>
                        </Card>

                        <Card title='Change Subscription Plan' mb='20px'>
                            <Box w='100%' p='20px'>
                                <Text mb='20px'>
                                    Changing the plan for this subscription will
                                    alter future payments. Please make sure you
                                    want to do this before proceeding.
                                </Text>
                                <Field
                                    component='select'
                                    options={[
                                        { name: 'Starter', id: 1 },
                                        { name: 'Pro', id: 2 },
                                    ]}
                                    valueKey='id'
                                    labelKey='name'
                                    placeholder='Change Plan'
                                    label='Change Plan'
                                    name='feature'
                                />
                            </Box>
                        </Card>
                        <Card title='Actions' mb='20px'>
                            <Box w='100%' p='20px'>
                                <Flex>
                                    {data?.active === 0 && (
                                        <PopConfirm
                                            onConfirm={() => activate()}
                                            title='Are you sure you want to activate this subscription?'
                                        >
                                            <Button
                                                variantColor='success'
                                                color='white'
                                            >
                                                Reactivate Subscription
                                            </Button>
                                        </PopConfirm>
                                    )}
                                    {data?.active === 1 && (
                                        <PopConfirm
                                            onConfirm={() => pause()}
                                            title='Are you sure you want to pause this subscription?'
                                        >
                                            <Button>Pause Subscription</Button>
                                        </PopConfirm>
                                    )}
                                    <PopConfirm
                                        onConfirm={() => cancel()}
                                        title='Are you sure you want to cancel this subscription?  This cannot be undone.'
                                    >
                                        <Button variantColor='error' ml='15px'>
                                            Cancel Subscription
                                        </Button>
                                    </PopConfirm>
                                </Flex>
                            </Box>
                        </Card>
                    </TabPanel>
                    <TabPanel>
                        <Box w='100%' p='20px'>
                            <SectionTitle>Payment History</SectionTitle>
                            <Grid templateColumns='repeat(3, 1fr)'>
                                {data &&
                                    payments?.map((payment, i) => (
                                        <>
                                            <Column>
                                                <Text pr='25px'>
                                                    {dayjs(
                                                        payment.payment_date
                                                    ).format('DD/MM/YYYY')}
                                                </Text>
                                            </Column>
                                            <Column>
                                                <Text
                                                    pr='25px'
                                                    fontWeight='semibold'
                                                >
                                                    {
                                                        payment.payment_status
                                                            ?.name
                                                    }
                                                </Text>
                                            </Column>
                                            <Column>
                                                <Text
                                                    pr='15px'
                                                    fontWeight='semibold'
                                                >
                                                    £{payment.price / 100}
                                                </Text>
                                            </Column>
                                        </>
                                    ))}
                            </Grid>
                        </Box>
                    </TabPanel>
                    <TabPanel>
                        <Box w='100%' p='20px'>
                            <SectionTitle>Upcoming Payments</SectionTitle>
                            <Grid templateColumns='repeat(3, 1fr)'>
                                {data &&
                                    future_payments?.map((payment, i) => (
                                        <>
                                            <Column>
                                                <Text pr='25px'>
                                                    {dayjs(
                                                        payment.payment_date
                                                    ).format('DD/MM/YYYY')}
                                                </Text>
                                            </Column>
                                            <Column>
                                                <Text
                                                    pr='25px'
                                                    fontWeight='semibold'
                                                >
                                                    {
                                                        payment.payment_status
                                                            ?.name
                                                    }
                                                </Text>
                                            </Column>
                                            <Column>
                                                <Text
                                                    pr='15px'
                                                    fontWeight='semibold'
                                                >
                                                    £{payment.price / 100}
                                                </Text>
                                            </Column>
                                        </>
                                    ))}
                            </Grid>
                        </Box>
                    </TabPanel>
                </TabPanels>
            </Tabs>
        </Box>
    );
};

const EditForm = () => {
    return (
        <EditView setup={setup}>
            <Fields />
        </EditView>
    );
};

export default EditForm;
